import { orderService, paymentService, storeService } from 'config/api';
import {
  mapDataForOrderReview,
  mapDataForOrderSubmit,
  mapDataForStoreInfo,
  transformProductsForOrderReview,
} from 'utils';
import { extractSubmitErrorMessage } from 'modules/clients/utils';
import { addNotification } from 'modules/shell/actions';
import { missingCardMessage } from 'modules/clients/constants';
import actionTypes from './actionTypes';

export const setDefaultShipmentAddress = (value) => {
  return {
    type: actionTypes.SET_SHIPMENT_DEFAULT,
    payload: value,
  };
};

export const setSelectedShipmentAddress = (id) => ({
  type: actionTypes.SET_SHIPMENT_SELECTED,
  payload: id,
});

/**
 * @param {Object} reviewParams
 * @param {Array} reviewParams.items - Optional items list that will be used for store pickup order review.
 * If no items provided, items from conciergeProducts will be used instead.
 * For more details see: https://neimanmarcus.atlassian.net/browse/OSP-4558
 */
export const orderReview =
  ({ items = [], farfetchCheck = false }) =>
  async (dispatch, getState) => {
    dispatch({
      type: actionTypes.ORDER_REVIEW_LOADING,
    });

    const { clientsModule } = getState();
    const { clientId, shippingAddress } = clientsModule.clientDetails?.data || {};

    const data = mapDataForOrderReview(clientsModule, farfetchCheck);

    if (!shippingAddress?.length) {
      // Reset shipmentMethods if no addresses were found
      dispatch({
        type: actionTypes.ORDER_REVIEW_SUCCESS,
        payload: {
          shipmentOptions: [],
          items: farfetchCheck ? clientsModule.cart?.farfetchConciergeProducts : clientsModule.cart.conciergeProducts,
        },
      });
      return;
    }

    try {
      const itemsLength = items.length;
      const orderData = itemsLength ? { ...data, items: transformProductsForOrderReview(items) } : data;
      const response = await orderService.post('/review', orderData, { clientId });

      if (!itemsLength) {
        dispatch({
          type: actionTypes.ORDER_REVIEW_SUCCESS,
          payload: {
            shipmentOptions: response.data.shipmentOptions,
            items: farfetchCheck ? clientsModule.cart?.farfetchConciergeProducts : clientsModule.cart.conciergeProducts,
          },
        });
      } else {
        dispatch({
          type: actionTypes.ORDER_REVIEW_STORE_PICKUP_SUCCESS,
          payload: {
            shipmentOptions: [response.data.shipmentOptions.find((method) => method.shipmentOption === 'SL3')],
            items,
          },
        });
      }
    } catch (err) {
      dispatch({
        type: actionTypes.ORDER_REVIEW_ERROR,
        payload: err,
      });
    }
  };

export const checkLoyaltyCardBalance = (giftCardNumber) => async (dispatch) => {
  try {
    const res = await paymentService.post('/addGiftCard', {
      giftCardNumber,
      isLoyalty: true,
    });
    dispatch({
      type: actionTypes.UPDATE_LOYALTY_CARD_BALANCE,
      payload: res.data,
    });
    return res.data.giftCardBalance.toFixed(2);
  } catch (error) {
    return '';
  }
};

export const addGiftCardPayment = (payload) => async (dispatch, getState) => {
  const { gcPayments } = getState().clientsModule.checkout;
  const isCardExists = gcPayments.find((p) => p.giftCardNumber === payload.giftCardNumber);
  let errorMessage = 'Card number does not match with PIN number';

  try {
    if (isCardExists) {
      errorMessage = 'This card already exists';
      throw new Error('This card already exists');
    }

    const { data } = await paymentService.post('/addGiftCard', payload, { httpClientOptions: { handleErrors: false } });
    dispatch({
      type: actionTypes.ADD_GIFT_CARD_PAYMENT_SUCCESS,
      payload: {
        ...payload,
        ...data,
        selected: data.giftCardBalance !== 0,
      },
    });
    return data;
  } catch (err) {
    dispatch(
      addNotification({
        id: `error-${Date.now()}`,
        message: errorMessage,
        type: 'error',
      }),
    );
  }
};

export const submitOrder =
  (clientId, data, bopsAddress = {}) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.SUBMIT_ORDER_LOADING,
    });

    try {
      const response = await orderService.post('/submit', mapDataForOrderSubmit({ ...data, bopsAddress }), {
        clientId,
        httpClientOptions: { handleErrors: false },
      });
      dispatch({
        type: actionTypes.SUBMIT_ORDER_SUCCESS,
        payload: response.data.orderId,
      });
    } catch (err) {
      const errorMessage = extractSubmitErrorMessage(err);
      dispatch(
        addNotification({
          id: `error-${Date.now()}`,
          message: errorMessage,
          type: 'error',
        }),
      );
      dispatch({
        type: actionTypes.SUBMIT_ORDER_ERROR,
        payload: err,
      });
    }
  };

export const applyPromo = (promoCodes) => {
  return {
    type: actionTypes.APPLY_PROMO,
    payload: promoCodes,
  };
};

export const removePromo = (promoCode) => {
  return {
    type: actionTypes.REMOVE_PROMO,
    payload: promoCode,
  };
};

export const resetPromo = () => {
  return {
    type: actionTypes.RESET_PROMO,
  };
};

export const clearOrderReview = () => {
  return {
    type: actionTypes.ORDER_REVIEW_CLEAR,
  };
};

export const updateGCPayments = (payload) => {
  return {
    type: actionTypes.UPDATE_GIFT_CARD_PAYMENTS,
    payload,
  };
};

export const preSubmitOrder = () => (dispatch) => {
  const err = new Error();
  err.message = missingCardMessage;

  dispatch({
    type: actionTypes.SUBMIT_ORDER_ERROR,
    payload: err,
  });

  dispatch({
    type: '@SHELL_ADD_NOTIFICATION',
    payload: {
      id: `http-${Date.now()}`,
      type: 'error',
      message: err.message,
    },
  });
};

export const getStoreInformation = (storeNum) => async (dispatch) => {
  let formattedNum = Number(storeNum);
  if (formattedNum < 10) {
    formattedNum = `0${formattedNum}`;
  }

  dispatch({
    type: actionTypes.STORE_INFORMATION_LOADING,
  });

  try {
    const { data } = await storeService.get(`/${formattedNum}`);
    dispatch({
      type: actionTypes.STORE_INFORMATION_SUCCESS,
      payload: mapDataForStoreInfo(data, storeNum),
    });
  } catch (error) {
    dispatch({
      type: actionTypes.STORE_INFORMATION_ERROR,
      payload: error,
    });
  }
};

export const setStoreInformation = (storeInfo, storeNum) => {
  return {
    type: actionTypes.STORE_INFORMATION_SUCCESS,
    payload: mapDataForStoreInfo(storeInfo, storeNum),
  };
};

export const searchStoreInformation = (searchParam) => async (dispatch) => {
  if (searchParam) {
    dispatch({ type: actionTypes.SEARCH_STORE_LOADING });
    try {
      const { data } = await storeService.get(`?freeFormAddress=${searchParam}`);
      dispatch({
        type: actionTypes.SEARCH_STORE_SUCCESS,
        payload: data,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.SEARCH_STORE_ERROR,
        payload: error,
      });
    }
  }
};

export const clearSelectedStore = () => ({
  type: actionTypes.RESET_STORE_INFORMATION,
});

export const setStorePickupMethod = (method) => ({
  type: actionTypes.SET_STORE_PICKUP_METHOD,
  payload: method,
});

/**
 * For BG Brand, if the sumtotal of all regular items in Checkout is more than $700, gift wrap is Free,
 * excluding sale and adjusted items from the sumtotal. (Business asked to override
 * the gift wrapping fee and total in checkout, order receipt and order confirmation page)
 * For more details see: https://neimanmarcus.atlassian.net/browse/OSP-9297
 */
export const setBgElevatedGiftPackagingMethod = (method) => ({
  type: actionTypes.SET_BG_ELEVATED_GIFT_PACKAGING_METHOD,
  payload: method,
});
